/**
 * The open intake's editable state: a local copy of title, schema and
 * branding, saved to the server on a debounce. Agent edits arrive through
 * `intakes` invalidations and replace the local copy when nothing is unsaved;
 * with unsaved edits the attorney is told instead, as `PlanPane` does.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import type {
  OutgoingIntake,
  OutgoingIntakeFormNode,
  OutgoingIntakeReminderSchedule
} from "@/api/types/outgoingIntake";

import {
  useOnRequestsInvalidate,
  useRequestsIde
} from "../context/RequestsIdeContext";
import { useRequestsData } from "../data/RequestsDataContext";

export type SaveState = "saved" | "dirty" | "saving" | "error";

/** Who gets the link, what it says, how long it lives, when it nudges. */
export type DraftLinkSettings = {
  recipient_email: string;
  attorney_note: string;
  expires_in_days: number;
  reminder_schedule: OutgoingIntakeReminderSchedule | null;
};

export const DEFAULT_EXPIRY_DAYS = 14;
export const DEFAULT_REMINDERS: OutgoingIntakeReminderSchedule = {
  days_after_send: [3, 7],
  days_before_expiry: 2
};

export const isEmail = (value: string): boolean =>
  /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.trim());

const settingsOf = (
  intake: OutgoingIntake | null,
  clientEmail: string | null
): DraftLinkSettings => ({
  recipient_email: intake?.recipient_email || clientEmail || "",
  attorney_note: intake?.attorney_note ?? "",
  expires_in_days: intake?.expires_in_days ?? DEFAULT_EXPIRY_DAYS,
  reminder_schedule:
    intake?.reminder_schedule === undefined
      ? DEFAULT_REMINDERS
      : intake.reminder_schedule
});

export type IntakeDraft = {
  intake: OutgoingIntake | null;
  loading: boolean;
  editable: boolean;
  title: string;
  schema: OutgoingIntakeFormNode[];
  enableFirmBranding: boolean | null;
  backgroundColor: string | null;
  settings: DraftLinkSettings;
  saveState: SaveState;
  /** The agent changed the intake while there were unsaved edits. */
  staleFromAgent: boolean;
  setTitle: (title: string) => void;
  setSchema: (schema: OutgoingIntakeFormNode[]) => void;
  setBranding: (patch: {
    enable_firm_branding?: boolean | null;
    intake_background_color?: string | null;
  }) => void;
  setSettings: (patch: Partial<DraftLinkSettings>) => void;
  /** Flush pending edits now (before Send, or when the tab closes). */
  flush: () => Promise<void>;
  reload: () => Promise<void>;
};

const SAVE_DEBOUNCE_MS = 900;

export const useIntakeDraft = (
  intakeId: string,
  clientEmail: string | null = null
): IntakeDraft => {
  const { clientId, invalidate } = useRequestsIde();
  const data = useRequestsData();
  const detail = data.detail(intakeId);
  const listed =
    data.intakes.find((i) => i.outgoing_intake_id === intakeId) ?? null;
  const intake = detail?.intake ?? listed;

  const [title, setTitleState] = useState(intake?.title ?? "");
  const [schema, setSchemaState] = useState<OutgoingIntakeFormNode[]>(
    intake?.schema ?? []
  );
  const [enableFirmBranding, setEnableFirmBranding] = useState<boolean | null>(
    intake?.enable_firm_branding ?? null
  );
  const [backgroundColor, setBackgroundColor] = useState<string | null>(
    intake?.intake_background_color ?? null
  );
  const [settings, setSettingsState] = useState<DraftLinkSettings>(() =>
    settingsOf(intake, clientEmail)
  );
  const [saveState, setSaveState] = useState<SaveState>("saved");
  const [staleFromAgent, setStaleFromAgent] = useState(false);
  const pending = useRef<{
    title?: string;
    form_schema?: unknown[];
    enable_firm_branding?: boolean;
    intake_background_color?: string | null;
    recipient_email?: string;
    attorney_note?: string;
    expires_in_days?: number;
    reminder_schedule?: OutgoingIntakeReminderSchedule | null;
  }>({});
  const timer = useRef<number | null>(null);
  const loadedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!detail) void data.loadDetail(intakeId);
  }, [data, detail, intakeId]);

  // Adopt the server copy on first load and whenever nothing is unsaved.
  useEffect(() => {
    if (!intake) return;
    const firstLoad = loadedFor.current !== intakeId;
    const clean =
      Object.keys(pending.current).length === 0 && saveState !== "saving";
    if (firstLoad || clean) {
      loadedFor.current = intakeId;
      setTitleState(intake.title ?? "");
      setSchemaState(intake.schema ?? []);
      setEnableFirmBranding(intake.enable_firm_branding ?? null);
      setBackgroundColor(intake.intake_background_color ?? null);
      setSettingsState(settingsOf(intake, clientEmail));
      setStaleFromAgent(false);
    } else {
      setStaleFromAgent(true);
    }
  }, [intake, intakeId]);

  const editable = intake?.status === "draft";

  const flush = useCallback(async () => {
    if (timer.current) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    const patch = pending.current;
    if (!Object.keys(patch).length) return;
    pending.current = {};
    setSaveState("saving");
    try {
      const updated = await OutgoingIntakesAPI.patch(clientId, intakeId, patch);
      data.applyIntake(updated);
      setSaveState(Object.keys(pending.current).length ? "dirty" : "saved");
      invalidate(["intakes"]);
    } catch (e) {
      // Edits made while this save was in flight are newer than the patch.
      pending.current = { ...patch, ...pending.current };
      setSaveState("error");
      toast.error(e instanceof Error ? e.message : "Couldn't save the intake");
    }
  }, [clientId, data, intakeId, invalidate]);

  const schedule = useCallback(() => {
    setSaveState("dirty");
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void flush(), SAVE_DEBOUNCE_MS);
  }, [flush]);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
      if (Object.keys(pending.current).length) void flush();
    },
    [flush]
  );

  const setTitle = useCallback(
    (next: string) => {
      setTitleState(next);
      pending.current.title = next.trim() || "Intake";
      schedule();
    },
    [schedule]
  );
  const setSchema = useCallback(
    (next: OutgoingIntakeFormNode[]) => {
      setSchemaState(next);
      pending.current.form_schema = next as unknown[];
      schedule();
    },
    [schedule]
  );
  const setBranding = useCallback(
    (patch: {
      enable_firm_branding?: boolean | null;
      intake_background_color?: string | null;
    }) => {
      if (patch.enable_firm_branding !== undefined) {
        setEnableFirmBranding(patch.enable_firm_branding);
        pending.current.enable_firm_branding = Boolean(
          patch.enable_firm_branding
        );
      }
      if (patch.intake_background_color !== undefined) {
        setBackgroundColor(patch.intake_background_color);
        pending.current.intake_background_color = patch.intake_background_color;
      }
      schedule();
    },
    [schedule]
  );

  const setSettings = useCallback(
    (patch: Partial<DraftLinkSettings>) => {
      setSettingsState((prev) => ({ ...prev, ...patch }));
      const { recipient_email, ...rest } = patch;
      const saveable: Partial<DraftLinkSettings> = rest;
      if (recipient_email !== undefined && isEmail(recipient_email))
        saveable.recipient_email = recipient_email.trim();
      if (!Object.keys(saveable).length) return;
      Object.assign(pending.current, saveable);
      schedule();
    },
    [schedule]
  );

  const reload = useCallback(async () => {
    pending.current = {};
    setSaveState("saved");
    setStaleFromAgent(false);
    loadedFor.current = null;
    await data.loadDetail(intakeId, { force: true });
  }, [data, intakeId]);

  useOnRequestsInvalidate(["intakes"], () => {
    void data.loadDetail(intakeId, { force: true });
  });

  return useMemo(
    () => ({
      intake,
      loading: !intake,
      editable: Boolean(editable),
      title,
      schema,
      enableFirmBranding,
      backgroundColor,
      settings,
      saveState,
      staleFromAgent,
      setTitle,
      setSchema,
      setBranding,
      setSettings,
      flush,
      reload
    }),
    [
      intake,
      editable,
      title,
      schema,
      enableFirmBranding,
      backgroundColor,
      settings,
      saveState,
      staleFromAgent,
      setTitle,
      setSchema,
      setBranding,
      setSettings,
      flush,
      reload
    ]
  );
};
